import { useRef, useState } from "react";
import { api, formatError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function UploadButton({ kind = "image", value, onChange, label, accept }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => fileRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // url is "/api/files/..." → make absolute
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(fullUrl, { name: file.name, size: file.size, type: file.type });
      toast.success(
        kind === "video" ? "Video uploaded" :
        kind === "file" ? "File uploaded" :
        "Image uploaded"
      );
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const defaultAccept = (
    kind === "video" ? "video/*" :
    kind === "file"
      ? ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.rtf,.zip,.json,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,text/markdown,application/rtf,application/zip"
      : "image/*"
  );

  return (
    <div className="space-y-2" data-testid={`upload-${kind}`}>
      <input
        ref={fileRef} type="file" className="hidden"
        accept={accept || defaultAccept}
        onChange={(e) => upload(e.target.files?.[0])}
      />
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" onClick={pick} disabled={busy} className="flex-1" data-testid={`upload-${kind}-btn`}>
          {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/>Uploading…</> : <><Upload className="h-4 w-4 mr-1.5"/>{label || `Upload ${kind}`}</>}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="icon" onClick={() => onChange("")} title="Remove" data-testid={`upload-${kind}-remove`}>
            <X className="h-4 w-4"/>
          </Button>
        )}
      </div>
      {value && kind === "image" && (
        <img src={value} alt="" className="h-20 w-full object-cover rounded-md border border-slate-200"/>
      )}
      {value && kind === "video" && (
        <div className="text-xs text-slate-500 truncate">📹 {value.split("/").pop()}</div>
      )}
      {value && kind === "file" && (
        <div className="text-xs text-slate-500 truncate">📎 {value.split("/").pop()}</div>
      )}
    </div>
  );
}
